// AddInventory — modal-embedded form for creating an Inventory row.
// Refactored 2026-05-05 (Phase 3.4, Class P) — header onto ModalFormHeader.
// AISC autofill (lookupShape) + 16 fields + 2 toggles preserved.

import React, { useState, useCallback, useRef } from "react";
import CTextField from "../CTextField/CTextField";
import { Button, Chip, Tooltip, Box, Stack, Grid, Typography } from "@mui/material";
import { Inventory2Outlined, CheckCircleOutline as CheckCircleOutlineIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import CSwitch from "../CSwitch/CSwitch";
import { createInventory } from "../../feature/inventorySlice";
import CSelect from "../CSelect/CSelect";
import { showErrorToast } from "../../utils/Toast";
import { lookupShape } from "../../utils/aiscShapeData";
import { ModalFormHeader, FieldLabel } from "../PaperForm";

// ─── Debounce helper ──────────────────────────────────────────────────────────
function useDebounce(fn, delay) {
  const timer = useRef(null);
  return useCallback(
    (...args) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => fn(...args), delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay]
  );
}

const AddInventory = (props) => {
  const dispatch = useDispatch();

  const [inventoryData, setInventoryData] = useState({
    ediStdNomenclature: "",
    aiscManualLabel: "",
    weight: "",
    depth: "",
    grade: "",
    poNumber: "",
    heatNumber: "",
    lengthReceivedFoot: "",
    lengthReceivedInch: "",
    quantity: "",
    poPulledFromNumber: "",
    lengthUsedFoot: "",
    lengthUsedInch: "",
    lengthRemainingFoot: "",
    lengthRemainingInch: "",
  });

  const [orderArrivedInFull, setOrderArrivedInFull] = useState(false);
  const [orderArrivedCMTR, setOrderArrivedCMTR] = useState(false);
  const [selectShape, setSelectedShape] = useState("");
  const [itemType, setItemType] = useState("");
  const [aiscMatched, setAiscMatched] = useState(false);

  const shapeData = ["2L", "C", "HP", "HSS", "L", "M", "MC", "MT", "PIPE", "PL", "PLT", "S", "ST", "W", "WT"];
  const selectItemType = ["stock", "job"];
  // W-31: CSelect reads item.value and item.name/label, so plain strings gave options with no text
  // and no value. The values are exactly the strings the server accepts (inventoryValidator VALID_SHAPES
  // and itemType "stock" | "job").
  const shapeOptions = shapeData.map((s) => ({ value: s, label: s, name: s }));
  const itemTypeOptions = selectItemType.map((s) => ({ value: s, label: s, name: s }));

  // ── AISC autofill on EDI nomenclature change ──────────────────────────────
  const runAiscLookup = useCallback((value) => {
    const match = lookupShape(value);
    if (match) {
      setInventoryData((prev) => ({
        ...prev,
        aiscManualLabel: match.label,
        weight: String(match.weight),
        depth: String(match.d),
      }));
      if (match.shape) setSelectedShape(match.shape);
      setAiscMatched(true);
    } else {
      setAiscMatched(false);
    }
  }, []);

  const debouncedLookup = useDebounce(runAiscLookup, 350);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInventoryData((prev) => ({ ...prev, [name]: value }));

    if (name === "ediStdNomenclature") {
      setAiscMatched(false);
      debouncedLookup(value);
    }
  };

  const handleSubmit = () => {
    if (!inventoryData.ediStdNomenclature.trim()) {
      showErrorToast("EDI Std Nomenclature is required.");
      return;
    }
    if (!selectShape) {
      showErrorToast("Please select a shape.");
      return;
    }
    if (!itemType) {
      // The server stores item type as required ("stock" or "job").
      showErrorToast("Please select an item type.");
      return;
    }
    const finalData = {
      ...inventoryData,
      orderArrivedCMTR,
      orderArrivedInFull,
      shape: selectShape,
      itemType,
    };
    // The inventory slice shows the result: success, or the server's refusal (no second toast here).
    dispatch(createInventory(finalData)).then((res) => {
      if (res.type === "createInventory/Inventory/fulfilled") {
        props.setOpen(false);
      }
    });
  };

  return (
    <Box sx={{ overflowY: "auto", maxHeight: 550, overflowX: "hidden", pr: 0.5 }}>
      <ModalFormHeader
        overline="SFM · Inventory"
        title="Add Inventory"
        icon={Inventory2Outlined}
        rightSlot={
          aiscMatched ? (
            <Tooltip title="Shape matched in AISC 16th Edition database — Label, Weight and Depth autofilled">
              <Chip
                icon={<CheckCircleOutlineIcon />}
                label="AISC Matched"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600, letterSpacing: "0.04em" }}
              />
            </Tooltip>
          ) : null
        }
      />

      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px"
              fullWidth
              onChange={handleChange}
              name="ediStdNomenclature"
              label="EDI Std Nomenclature"
              value={inventoryData.ediStdNomenclature}
              placeholder="e.g. W8X31, HSS4X4X1/4, L3X3X3/8"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px"
              fullWidth
              onChange={handleChange}
              name="aiscManualLabel"
              label="AISC Manual Label"
              value={inventoryData.aiscManualLabel}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FieldLabel>Shape</FieldLabel>
            <CSelect
              data={shapeOptions}
              label=""
              initialValue={selectShape}
              setinitialValue={setSelectedShape}
              margin="0px"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Item Type</FieldLabel>
            <CSelect
              data={itemTypeOptions}
              label=""
              initialValue={itemType}
              setinitialValue={setItemType}
              margin="0px"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="weight" label="Weight (lbs/ft)"
              value={inventoryData.weight}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth type="Number" onChange={handleChange}
              name="depth" label="Depth (in)"
              value={inventoryData.depth}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="grade" label="Grade"
              value={inventoryData.grade}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="poNumber" label="PO Number"
              value={inventoryData.poNumber}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="heatNumber" label="Heat Number"
              value={inventoryData.heatNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="poPulledFromNumber" label="PO Pulled From Number"
              value={inventoryData.poPulledFromNumber}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthReceivedFoot" label="Length Received (ft)"
              value={inventoryData.lengthReceivedFoot}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthReceivedInch" label="Length Received (in)"
              value={inventoryData.lengthReceivedInch}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthUsedFoot" label="Length Used (ft)"
              value={inventoryData.lengthUsedFoot}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthUsedInch" label="Length Used (in)"
              value={inventoryData.lengthUsedInch}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthRemainingFoot" label="Length Remaining (ft)"
              value={inventoryData.lengthRemainingFoot}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthRemainingInch" label="Length Remaining (in)"
              value={inventoryData.lengthRemainingInch}
            />
          </Grid>

          <Grid item xs={12}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="quantity" label="Quantity"
              value={inventoryData.quantity}
            />
          </Grid>
        </Grid>

        {/* Toggles */}
        <Box
          sx={{
            display: "flex", flexDirection: { xs: "column", sm: "row" },
            gap: 2, p: 2, border: "1px solid", borderColor: "divider",
            borderRadius: 2, bgcolor: "#fafafa",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
              Order Arrived In Full
            </Typography>
            <CSwitch
              active={orderArrivedInFull}
              name="orderArrivedInFull"
              setSwitch={() => setOrderArrivedInFull(!orderArrivedInFull)}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
              Order Arrived CMTR
            </Typography>
            <CSwitch
              active={orderArrivedCMTR}
              name="orderArrivedCMTR"
              setSwitch={() => setOrderArrivedCMTR(!orderArrivedCMTR)}
            />
          </Stack>
        </Box>

        <Button
          disabled={props.isLoading}
          onClick={handleSubmit}
          variant="contained"
          sx={{
            width: "100%",
            mt: 1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: "0.01em",
          }}
        >
          {props.isLoading ? "Loading…" : "Create Inventory"}
        </Button>
      </Stack>
    </Box>
  );
};

export default AddInventory;
